(() => {
  if (window.__sizeChartDrawerInitialized === true) return;
  window.__sizeChartDrawerInitialized = true;

  const getPickerKey = (picker) => {
    if (!(picker instanceof HTMLElement)) return '';
    const sectionId = picker.dataset.sectionId || 'section';
    const blockId = picker.dataset.blockId || 'block';
    return `${sectionId}:${blockId}`;
  };

  const getSingletonDrawer = () =>
    document.querySelector('[data-size-chart-drawer-singleton], #size-chart-drawer');

  const updateBodyScrollLock = () => {
    const isOpen =
      document.querySelector('[data-size-chart-drawer][open]') !== null ||
      document.querySelector('[data-size-chart-drawer][aria-hidden="false"]') !== null;
    document.body.classList.toggle('size-chart-drawer-open', isOpen);
  };

  const updateScrollbarThumb = (wrap, scrollbar, thumb) => {
    const maxScroll = wrap.scrollWidth - wrap.clientWidth;
    const hasOverflow = maxScroll > 0;
    if (!hasOverflow) {
      const fullWidth = Math.max(scrollbar.clientWidth - 6, 56);
      thumb.style.width = `${fullWidth}px`;
      thumb.style.transform = 'translateX(0)';
      thumb.disabled = true;
      return;
    }

    const trackWidth = scrollbar.clientWidth;
    const minThumbWidth = 56;
    const thumbWidth = Math.max(minThumbWidth, (wrap.clientWidth / wrap.scrollWidth) * trackWidth);
    const maxThumbOffset = Math.max(trackWidth - thumbWidth, 0);
    const progress = maxScroll > 0 ? wrap.scrollLeft / maxScroll : 0;
    const thumbOffset = maxThumbOffset * progress;

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${thumbOffset}px)`;
    thumb.disabled = false;
  };

  const initDrawerScrollbars = (drawer) => {
    drawer.querySelectorAll('[data-size-chart-table-wrap]').forEach((wrap) => {
      const scrollbar = wrap.parentElement?.querySelector('[data-size-chart-scrollbar]');
      const thumb = scrollbar?.querySelector('[data-size-chart-scrollbar-thumb]');
      if (!(wrap instanceof HTMLElement) || !(scrollbar instanceof HTMLElement) || !(thumb instanceof HTMLButtonElement))
        return;

      const syncThumb = () => updateScrollbarThumb(wrap, scrollbar, thumb);

      if (scrollbar.dataset.scrollbarInit !== 'true') {
        scrollbar.dataset.scrollbarInit = 'true';

        wrap.addEventListener('scroll', syncThumb, { passive: true });
        window.addEventListener('resize', syncThumb);

        let dragOffsetX = 0;
        let isDragging = false;

        thumb.addEventListener('pointerdown', (event) => {
          event.preventDefault();
          isDragging = true;
          dragOffsetX = event.clientX - thumb.getBoundingClientRect().left;
          thumb.setPointerCapture(event.pointerId);
        });

        thumb.addEventListener('pointermove', (event) => {
          if (thumb.disabled || !isDragging) return;
          const maxScroll = wrap.scrollWidth - wrap.clientWidth;
          if (maxScroll <= 0) return;

          const trackRect = scrollbar.getBoundingClientRect();
          const thumbWidth = thumb.offsetWidth;
          const maxThumbOffset = Math.max(trackRect.width - thumbWidth, 0);
          const rawX = event.clientX - trackRect.left - dragOffsetX;
          const clampedX = Math.min(Math.max(rawX, 0), maxThumbOffset);
          const progress = maxThumbOffset > 0 ? clampedX / maxThumbOffset : 0;
          wrap.scrollLeft = progress * maxScroll;
        });

        const endDrag = (event) => {
          if (!isDragging) return;
          isDragging = false;
          if (thumb.hasPointerCapture(event.pointerId)) {
            thumb.releasePointerCapture(event.pointerId);
          }
        };

        thumb.addEventListener('pointerup', endDrag);
        thumb.addEventListener('pointercancel', endDrag);

        scrollbar.addEventListener('click', (event) => {
          if (thumb.disabled || event.target === thumb) return;

          const maxScroll = wrap.scrollWidth - wrap.clientWidth;
          if (maxScroll <= 0) return;

          const trackRect = scrollbar.getBoundingClientRect();
          const thumbWidth = thumb.offsetWidth;
          const clickX = event.clientX - trackRect.left - thumbWidth / 2;
          const maxThumbOffset = Math.max(trackRect.width - thumbWidth, 0);
          const clampedX = Math.min(Math.max(clickX, 0), maxThumbOffset);
          const progress = maxThumbOffset > 0 ? clampedX / maxThumbOffset : 0;

          wrap.scrollTo({ left: progress * maxScroll, behavior: 'smooth' });
        });
      }

      requestAnimationFrame(syncThumb);
    });
  };

  const setActiveTab = (drawer, tabsContainer, key) => {
    const tabs = Array.from(tabsContainer.querySelectorAll('[data-size-chart-tab]'));
    if (tabs.length === 0) return;

    const targetTab =
      tabs.find((tab) => tab.dataset.sizeChartKey === key) || tabs.find((tab) => tab.classList.contains('is-active')) || tabs[0];
    const targetKey = targetTab?.dataset.sizeChartKey;
    const panelsParent = tabsContainer.parentElement;
    const panels = Array.from(panelsParent?.querySelectorAll('[data-size-chart-panel]') || []);

    tabs.forEach((tab) => {
      const isActive = tab === targetTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    panels.forEach((panel) => {
      const isActive = panel.dataset.sizeChartKey === targetKey;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    requestAnimationFrame(() => initDrawerScrollbars(drawer));
  };

  const initDrawerTabs = (drawer) => {
    drawer.querySelectorAll('[data-size-chart-tabs]').forEach((tabsContainer) => {
      tabsContainer.querySelectorAll('[data-size-chart-tab]').forEach((tab) => {
        if (tab.dataset.tabInit === 'true') return;
        tab.dataset.tabInit = 'true';
        tab.addEventListener('click', () => setActiveTab(drawer, tabsContainer, tab.dataset.sizeChartKey));
      });

      const existingActive = tabsContainer.querySelector('[data-size-chart-tab].is-active');
      const existingKey = existingActive?.dataset.sizeChartKey;
      setActiveTab(drawer, tabsContainer, existingKey);
    });
  };

  const closeDrawer = (drawer) => {
    if (!(drawer instanceof HTMLElement)) return;

    drawer.classList.remove('is-open');

    if (drawer instanceof HTMLDialogElement) {
      if (!drawer.open) return;
      drawer.close();
    } else {
      if (drawer.getAttribute('aria-hidden') === 'true') return;
      drawer.setAttribute('aria-hidden', 'true');
    }

    const trigger = drawer.__activeSizeChartTrigger;
    if (trigger instanceof HTMLElement) trigger.focus();
    updateBodyScrollLock();
  };

  /**
   * Find the content source (template) for the given trigger.
   * Prefer exact picker key; fallback to product ID when inside quick-add.
   */
  const findContentSource = (trigger) => {
    const picker = trigger.closest('variant-picker, variant-picker-v2');
    const pickerKey = getPickerKey(picker);
    if (!pickerKey) return null;

    let source = document.querySelector(
      `[data-size-chart-content][data-size-chart-picker-key="${pickerKey}"]`
    );
    if (source) return source;

    const isInQuickAdd = trigger.closest('quick-add-dialog, #quick-add-modal-content');
    if (!source && isInQuickAdd && picker) {
      const productId = picker.dataset.productId;
      if (productId) {
        const all = document.querySelectorAll('[data-size-chart-content]');
        source = Array.from(all).find(
          (el) => (el.getAttribute('data-size-chart-product-id') || '') === productId
        ) || null;
      }
    }
    return source;
  };

  /**
   * Update the singleton drawer with content from the matching template and open it.
   */
  const openDrawerWithContent = (drawer, trigger) => {
    if (!(drawer instanceof HTMLElement)) return;

    const contentSource = findContentSource(trigger);
    if (!contentSource) return;

    const contentContainer = drawer.querySelector('[data-size-chart-drawer-content]');
    if (!contentContainer) return;

    contentContainer.replaceChildren(...Array.from(contentSource.childNodes).map((n) => n.cloneNode(true)));

    drawer.__activeSizeChartTrigger = trigger;

    if (drawer instanceof HTMLDialogElement) {
      if (!drawer.open) {
        drawer.showModal();
      }
    } else {
      drawer.setAttribute('aria-hidden', 'false');
    }

    // Trigger slide-in transition in a separate frame for smoother animation
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
    });

    initDrawerTabs(drawer);
    initDrawerScrollbars(drawer);

    drawer.querySelector('[data-size-chart-close]')?.focus();
    updateBodyScrollLock();
  };

  const initSingletonDrawer = (drawer) => {
    if (!(drawer instanceof HTMLElement) || drawer.dataset.drawerInit === 'true') return;
    drawer.dataset.drawerInit = 'true';

    drawer.addEventListener('click', (event) => {
      const closeButton = event.target.closest('[data-size-chart-close]');
      if (closeButton) {
        closeDrawer(drawer);
        return;
      }

      if (drawer instanceof HTMLDialogElement) {
        const panel = drawer.querySelector('.size-chart-drawer__panel');
        if (!(panel instanceof HTMLElement)) return;

        const rect = panel.getBoundingClientRect();
        const { clientX, clientY } = event;
        const isInPanel =
          clientX >= rect.left &&
          clientX <= rect.right &&
          clientY >= rect.top &&
          clientY <= rect.bottom;

        if (!isInPanel) {
          closeDrawer(drawer);
        }
      }
    });

    drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeDrawer(drawer);
      }
    });
  };

  // After main morph completes, refresh drawer if open or close if no size chart exists
  window.addEventListener('pdp-breadcrumb:refresh', () => {
    const drawer = getSingletonDrawer();
    if (!drawer) return;
    const isOpen =
      (drawer instanceof HTMLDialogElement && drawer.open) ||
      drawer.getAttribute('aria-hidden') === 'false';
    if (!isOpen) return;

    const contentSource = document.querySelector('[data-size-chart-content]');
    if (!contentSource || !contentSource.hasChildNodes()) {
      closeDrawer(drawer);
      return;
    }

    const contentContainer = drawer.querySelector('[data-size-chart-drawer-content]');
    if (!contentContainer) return;
    contentContainer.replaceChildren(...Array.from(contentSource.childNodes).map((n) => n.cloneNode(true)));
    initDrawerTabs(drawer);
    initDrawerScrollbars(drawer);
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-size-chart-trigger]');
    if (trigger) {
      const drawer = getSingletonDrawer();
      if (drawer) openDrawerWithContent(drawer, trigger);
      return;
    }

    const closeButton = event.target.closest('[data-size-chart-close]');
    if (closeButton) {
      const drawer = closeButton.closest('[data-size-chart-drawer]');
      if (drawer) closeDrawer(drawer);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const drawer = getSingletonDrawer();
    if (!drawer) return;

    if (drawer instanceof HTMLDialogElement) {
      if (drawer.open) closeDrawer(drawer);
    } else if (drawer.getAttribute('aria-hidden') === 'false') {
      closeDrawer(drawer);
    }
  });

  const drawer = getSingletonDrawer();
  if (drawer) initSingletonDrawer(drawer);

  const observer = new MutationObserver(() => {
    const singleton = getSingletonDrawer();
    if (singleton && singleton.dataset.drawerInit !== 'true') {
      initSingletonDrawer(singleton);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
